import React, { Component } from 'react';
import PropTypes from 'prop-types';
import LoginHeader from '../../LoginHeader/LoginHeader';
import LoginFooter from '../LoginFooter/LoginFooter';
import PhoneInput from '../../PhoneInput/PhoneInput';
import { ApolloConsumer } from 'react-apollo';
import StudentQueries from '../../../graphql-operations/students.graphql';
import ErrorHandlingService from '../../../services/error-handling.service';

const OneLineInput = ({ label, name, required, ...props }) => {
    return (
        <label htmlFor={name}>
            <div className={`tx-label${required ? ' tx-label--required' : ''}`}>
                {label}
            </div>
            <input
                className="tx-input"
                {...props} />
        </label>
    );
};

OneLineInput.propTypes = {
    label: PropTypes.string,
    props: PropTypes.any,
};

class LogInForm extends Component {
    constructor(props) {
        super(props);
        this.handleSamePhoneNumberToggle = this.handleSamePhoneNumberToggle.bind(this);
        this.handleCreatingNewStudentToggle = this.handleCreatingNewStudentToggle.bind(this);
        this.inputDebounceTimeout = null;
        this.state = {
            isStudentValidated: true,
            isCreatingNewStudent: false,
            shouldUseSamePhone: false,
            formData: {},
        };
    }

    handleInputChange(data) {
        clearTimeout(this.inputDebounceTimeout);
        this.inputDebounceTimeout = setTimeout(() => this.setState(state => ({ formData: Object.assign(state.formData, data) })), 300);
    }

    handleSamePhoneNumberToggle() {
        this.setState(state => ({ shouldUseSamePhone: !state.shouldUseSamePhone }));
    }

    handleCreatingNewStudentToggle() {
        this.setState(state => ({ isCreatingNewStudent: !state.isCreatingNewStudent }));
    }

    handleSubmit($event, client, meeting, afterSubmitCallback) {
        $event.preventDefault();
        if (!this.state.isCreatingNewStudent) {
            client.query({
                query: StudentQueries.STUDENT_QUERY,
                variables: { email: this.state.formData.email, mobilePhone: this.state.formData.mobilePhone, studentId: this.state.formData.id },
            })
            .then(({ data }) => this.validateStudent(data))
            .then(({ data }) => this.completeMeetingDataAndNavigate(meeting, data, afterSubmitCallback))
            .catch(this.handleError);
        } else {
            const { firstName, lastName, email, phone, mobilePhone, preferredPhoneNumber } = this.state.formData;
            client.mutate({
                mutation: StudentQueries.STUDENT_MUTATION,
                variables: { firstName, lastName, email, phone, mobilePhone: mobilePhone || preferredPhoneNumber },
            })
            .then(({ data }) => this.completeMeetingDataAndNavigate(meeting, data, afterSubmitCallback))
            .catch(this.handleError);
        }
    }

    validateStudent(data) {
        if (data.loading) return Promise.reject();
        if (!data.student) return Promise.reject(this.setState({ isStudentValidated: false }));
        return Promise.resolve({ data: { student: data.student } });
    }

    completeMeetingDataAndNavigate(meeting, data, callback) {
        Object.assign(meeting, {
            preferredPhoneNumber: data.student.mobilePhone || data.student.phone || this.state.formData.preferredPhoneNumber,
            student: data.student,
            studentId: data.student.id,
        });
        callback();
    }

    handleError(error) {
        ErrorHandlingService.handleException(error);
    }

    render() {
        const { afterSubmitCallback, meeting, roomSigninFields } = this.props;
        return (
            <ApolloConsumer>
            {client => (
                <form onSubmit={$event => this.handleSubmit($event, client, meeting, afterSubmitCallback)} className="grid-y medium-grid-frame grid-padding-x tx-kiosk__content">
                    <LoginHeader
                        title="Log In to Register"
                        rightContent={
                        <a
                            href="https://www.google.com"/* <-- TODO: Replace with real 'Need Help' link */
                            className="tx-kiosk__link">Need Help?
                        </a>}
                    />
                    <div className="cell auto cell-block-container">
                        <div className="cell auto cell-block-container cell-block-y tx-kiosk__form">
                            {!roomSigninFields.value.useIdField ? '' :
                                <OneLineInput
                                    label={roomSigninFields.value.idFieldLabel || 'Student ID'}
                                    aria-label="Student ID"
                                    name="student-id"
                                    required={roomSigninFields.value.useIdField}
                                    onChange={({ target }) => this.handleInputChange({ id: target.value })}
                                    type="text" />}
                            {!roomSigninFields.value.useEmailField ? '' :
                                <OneLineInput
                                    label={roomSigninFields.value.emailFieldLabel || 'Email'}
                                    name="student-email"
                                    placeholder=""
                                    required={roomSigninFields.value.useEmailField}
                                    onChange={({ target }) => this.handleInputChange({ email: target.value }, client)}
                                    type="email" />}
                            {!roomSigninFields.value.usePhoneField ? '' :
                                <label htmlFor="student-number">
                                    <div className="tx-label tx-label--required">
                                        {roomSigninFields.value.phoneFieldLabel || 'Phone'}
                                    </div>
                                    <PhoneInput
                                        aria-label="Enter your phone number"
                                        name="student-number"
                                        placeholder="(###) ###-####"
                                        required={roomSigninFields.value.usePhoneField}
                                        onChange={({ target }) => this.handleInputChange({ mobilePhone: target.value, phone: target.value })} />
                                </label>}
                            <label htmlFor="sms-number">
                                <div className="tx-label tx-label--required">Sms Number</div>
                                {!roomSigninFields.value.usePhoneField ? '' :
                                    <div>
                                        <div className="tx-input-switch">
                                            <div className="tx-input-switch__label-group">
                                                <label className="tx-input-switch__label" htmlFor="use-same-phone">Use same number</label>
                                            </div>
                                            <input
                                                aria-label="Use same phone"
                                                className="tx-input-switch__switch"
                                                name="use-same-phone"
                                                onClick={this.handleSamePhoneNumberToggle}
                                                type="checkbox" />
                                        </div>
                                    </div>}
                                {this.state.shouldUseSamePhone ? '' :
                                    <PhoneInput
                                        aria-label="Enter your mobile number here"
                                        name="number"
                                        placeholder="(###) ###-####"
                                        required={!this.state.shouldUseSamePhone}
                                        onChange={({ target }) => this.handleInputChange({ preferredPhoneNumber: target.value })} /> }
                                <small className="tx-helper tx-helper--neg-top">
                                    We will send a text message to this number with confirmation/cancellation notices
                                </small>
                            </label>

                            {this.state.isStudentValidated ? '' :
                            <div>
                                {!this.state.isCreatingNewStudent ?
                                    <div className="tx-alert-block tx-alert-block--yellow">
                                        <span className="tx-alert-block__icon icon icon--warning" />
                                        <div className="tx-alert-block__content">
                                            We couldn&#39;t find a record based on what you entered. Click <button className="button--link" onClick={this.handleCreatingNewStudentToggle} type="button"> here </button> to create a new record.
                                        </div>
                                    </div>
                                    : '' }
                                {!this.state.isCreatingNewStudent ? '' :
                                <div>
                                    <div className="cell medium-6">
                                        <OneLineInput
                                            label="First Name"
                                            aria-label="New Student First Name"
                                            name="newStudentData-first-name"
                                            placeholder="Enter your first name here"
                                            required={!this.state.isStudentValidated}
                                            onChange={({ target }) => this.handleInputChange({ firstName: target.value })}
                                            type="text" />
                                    </div>
                                    <div className="cell medium-6">
                                        <OneLineInput
                                            label="Last Name"
                                            aria-label="New Student Last Name"
                                            name="newStudentData-last-name"
                                            placeholder="Enter your last name here"
                                            required={!this.state.isStudentValidated}
                                            onChange={({ target }) => this.handleInputChange({ lastName: target.value })}
                                            type="text" />
                                    </div>
                                    {roomSigninFields.value.useEmailField ? '' :
                                        <OneLineInput
                                            label="Email"
                                            aria-label="New Student Email"
                                            name="newStudentData-email"
                                            placeholder="Enter your email here"
                                            required={!this.state.isStudentValidated && !roomSigninFields.value.useEmailField}
                                            onChange={({ target }) => this.handleInputChange({ email: target.value })}
                                            type="text" />}
                                    {roomSigninFields.value.usePhoneField ? '' :
                                        <label htmlFor="newStudentData-phone">
                                            <div className="tx-label tx-label--required">Phone</div>
                                            <PhoneInput
                                                aria-label="New Student Phone"
                                                name="newStudentData-phone"
                                                placeholder="(###) ###-####"
                                                required={!this.state.isStudentValidated && !roomSigninFields.value.usePhoneField}
                                                onChange={({ target }) => this.handleInputChange({ mobilePhone: target.value, phone: target.value })} />
                                        </label>}
                                </div>}
                            </div>}

                        </div>
                    </div>
                    <LoginFooter buttonText="Continue" type="submit" />
                </form>
            )}
            </ApolloConsumer>
        );
    }
}

export default LogInForm;

LogInForm.propTypes = {
    afterSubmitCallback: PropTypes.func,
    meeting: PropTypes.any,
    roomSigninFields: PropTypes.object,
};



// WEBPACK FOOTER //
// ./src/components/login-flow-components/LogInForm/LogInForm.jsx